<template>
  <div>
    <div
      class="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2"
    >
      <h6 class="mb-0">{{ t("ecolabels.labels.source.devices") }}</h6>
      <div v-if="editable" class="d-flex gap-1">
        <button
          v-if="source.devices.length === 0"
          type="button"
          class="btn btn-sm btn-phoenix-secondary text-nowrap"
          :disabled="busy"
          @click="detectAgain"
        >
          <font-awesome-icon icon="sync" class="me-1" />
          {{ t("ecolabels.labels.source.detect_again") }}
        </button>
        <button
          type="button"
          class="btn btn-sm btn-phoenix-secondary text-nowrap"
          :disabled="busy"
          @click="pickerOpen = !pickerOpen"
        >
          <font-awesome-icon icon="plus" class="me-1" />
          {{ t("ecolabels.labels.source.add_device") }}
        </button>
      </div>
    </div>

    <div v-if="pickerOpen && editable" class="card border-primary mb-2">
      <div class="card-body p-2">
        <label :for="searchId" class="visually-hidden">
          {{ t("ecolabels.labels.source.search_devices") }}
        </label>
        <InputText
          :id="searchId"
          v-model="searchQuery"
          class="w-100 mb-2"
          size="small"
          :maxlength="MAX_QUERY_LENGTH"
          :placeholder="t('ecolabels.labels.source.search_devices')"
        />
        <LoadingIndicatorComponent v-if="catalogLoading" />
        <div v-else class="picker-list">
          <button
            v-for="device in pickerDevices"
            :key="device.id"
            type="button"
            class="btn btn-light w-100 text-start d-flex align-items-center gap-2 mb-1"
            :disabled="busy"
            @click="linkDevice(device.id)"
          >
            <span class="me-auto">
              <span class="fw-semibold">{{ device.name }}</span>
              <small class="text-body-secondary ms-2">
                {{ formatCountryWithFlag(device.country) }}
              </small>
            </span>
            <span
              class="badge"
              :class="deviceStatusPill(device.status).pillClass"
            >
              {{ t(deviceStatusPill(device.status).key) }}
            </span>
          </button>
          <div
            v-if="pickerDevices.length === 0"
            class="text-body-secondary text-center small py-2"
          >
            {{ t("ecolabels.labels.source.no_devices_found") }}
          </div>
        </div>
      </div>
    </div>

    <ul v-if="source.devices.length > 0" class="list-group">
      <li
        v-for="link in source.devices"
        :key="link.device_id"
        class="list-group-item device-row"
      >
        <div class="device-row-name">
          <div class="fw-semibold text-break">
            {{
              link.device_name ?? t("ecolabels.labels.source.deleted_device")
            }}
          </div>
          <small class="text-body-secondary">
            {{ deviceDetails(link.device_country, link.renewable_type) }}
          </small>
        </div>
        <div class="device-row-controls">
          <span
            v-if="link.device_status !== null"
            class="badge"
            :class="deviceStatusPill(link.device_status).pillClass"
          >
            {{ t(deviceStatusPill(link.device_status).key) }}
          </span>
          <div v-if="editable && source.devices.length > 1" class="mwh-input">
            <label :for="mwhInputId(link.device_id)" class="visually-hidden">
              {{ t("ecolabels.labels.source.mwh_attributed") }}
            </label>
            <div class="input-group input-group-sm">
              <input
                :id="mwhInputId(link.device_id)"
                v-model="mwhDrafts[link.device_id]"
                class="form-control text-end tabular-nums"
                :class="{
                  'is-invalid': !isWholeMwh(mwhDrafts[link.device_id]),
                }"
                type="number"
                inputmode="numeric"
                min="0"
                step="1"
                @change="saveAttributions"
              />
              <span class="input-group-text">{{ t("common.mwh") }}</span>
            </div>
          </div>
          <span
            v-else-if="
              source.devices.length > 1 && link.mwh_attributed !== null
            "
            class="tabular-nums"
          >
            {{ formatNumberWithSpaces(link.mwh_attributed) }}
            {{ t("common.mwh") }}
          </span>
          <button
            v-if="editable"
            type="button"
            class="btn btn-link btn-sm text-danger p-0"
            :disabled="busy"
            :title="t('ecolabels.labels.source.remove_device')"
            :aria-label="t('ecolabels.labels.source.remove_device')"
            @click="unlinkDevice(link.device_id)"
          >
            <font-awesome-icon icon="times" />
          </button>
        </div>
      </li>
    </ul>

    <template v-else-if="editable">
      <p class="small text-body-secondary mb-1">
        {{
          t(
            candidates.length > 0
              ? "ecolabels.labels.source.candidates"
              : "ecolabels.labels.source.no_candidates"
          )
        }}
      </p>
      <ul v-if="candidates.length > 0" class="list-group">
        <li
          v-for="candidate in candidates"
          :key="candidate.device_id"
          class="list-group-item d-flex flex-wrap align-items-center gap-2"
        >
          <div class="me-auto min-w-0">
            <div class="fw-semibold text-break">{{ candidate.name }}</div>
            <small class="text-body-secondary">
              {{ deviceDetails(candidate.country, candidate.renewable_type) }}
            </small>
            <div class="d-flex flex-wrap gap-1 mt-1">
              <span class="badge bg-body-secondary text-body">
                {{
                  t("ecolabels.labels.source.score", {
                    score: Math.round(candidate.score * 100),
                  })
                }}
              </span>
              <span
                v-for="reason in candidate.reasons"
                :key="reason"
                class="badge bg-light text-dark border"
              >
                {{ reasonText(reason) }}
              </span>
            </div>
          </div>
          <span
            class="badge"
            :class="deviceStatusPill(candidate.status).pillClass"
          >
            {{ t(deviceStatusPill(candidate.status).key) }}
          </span>
          <button
            type="button"
            class="btn btn-sm btn-outline-primary"
            :disabled="busy"
            @click="linkDevice(candidate.device_id)"
          >
            {{ t("ecolabels.labels.source.link") }}
          </button>
        </li>
      </ul>
    </template>
  </div>
</template>

<script setup lang="ts">
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import { formatNumberWithSpaces } from "@/composables/formatUtils";
import { useServerDraft } from "@/composables/useServerDraft";
import { formatCountryWithFlag } from "@/utils/countries";
import { compact } from "lodash-es";
import InputText from "primevue/inputtext";
import { computed, inject, ref, useId, watch } from "vue";
import { useI18n } from "vue-i18n";
import { ecolabelsApi } from "../api/ecolabelsApi";
import type {
  CancellationStatementDeviceLink,
  EcolabelDevice,
} from "../api/ecolabelsTypes";
import { MAX_QUERY_LENGTH, searchEcolabelDevices } from "../deviceSearch";
import {
  deviceStatusPill,
  isWholeMwh,
  matchReasonMessage,
  mwhOf,
} from "./labelDisplay";
import { labelsApi } from "./labelsApi";
import type { DeviceCandidate, LabelSource } from "./labelsTypes";
import { sourceWritesKey } from "./useBusyAction";

const MAX_PICKER_DEVICES = 20;

const props = defineProps<{
  labelId: number;
  source: LabelSource;
  editable: boolean;
  catalog: EcolabelDevice[];
  catalogLoading: boolean;
}>();

const emit = defineEmits<{
  changed: [];
}>();

const { t } = useI18n();
const searchId = useId();
const mwhIdPrefix = useId();

const mwhDrafts = useServerDraft(
  () => ({
    id: props.source.id,
    links: props.source.devices.map((link) => [
      link.device_id,
      link.mwh_attributed,
    ]),
  }),
  ({ links }) =>
    Object.fromEntries(
      links.map(([deviceId, mwh]) => [
        deviceId,
        mwh === null ? "" : String(mwh),
      ])
    ) as Record<number, string>
);
const pickerOpen = ref(false);
const searchQuery = ref("");
const detectedCandidates = ref<DeviceCandidate[] | null>(null);

const candidates = computed(
  () => detectedCandidates.value ?? props.source.candidates
);
const pickerDevices = computed(() => {
  const linked = new Set(props.source.devices.map((link) => link.device_id));
  return searchEcolabelDevices(props.catalog, searchQuery.value)
    .filter((device) => !linked.has(device.id))
    .slice(0, MAX_PICKER_DEVICES);
});

watch(
  () => props.source.devices.length,
  () => {
    detectedCandidates.value = null;
  }
);

async function linkDevice(deviceId: number) {
  pickerOpen.value = false;
  searchQuery.value = "";
  await saveDeviceLinks([
    ...currentLinks(),
    { device_id: deviceId, mwh_attributed: null },
  ]);
}

async function unlinkDevice(deviceId: number) {
  await saveDeviceLinks(
    currentLinks().filter((link) => link.device_id !== deviceId)
  );
}

async function saveAttributions() {
  if (!Object.values(mwhDrafts.value).every(isWholeMwh)) return;
  await saveDeviceLinks(currentLinks());
}

async function saveDeviceLinks(links: CancellationStatementDeviceLink[]) {
  await run(async () => {
    await ecolabelsApi.updateStatementDevices(props.source.id, links);
    emit("changed");
  });
}

async function detectAgain() {
  await run(async () => {
    const response = await labelsApi.detectSourceDevices(
      props.labelId,
      props.source.id
    );
    detectedCandidates.value = response.data.candidates;
  });
}

function currentLinks(): CancellationStatementDeviceLink[] {
  return props.source.devices.map((link) => ({
    device_id: link.device_id,
    mwh_attributed: mwhOf(mwhDrafts.value[link.device_id] ?? ""),
  }));
}

function reasonText(reason: string): string {
  const message = matchReasonMessage(reason);
  return message === null ? reason : t(message.key, message.params);
}

function deviceDetails(
  country: string | null,
  renewableType: string | null
): string {
  return compact([
    country && formatCountryWithFlag(country),
    renewableType &&
      t(`ecolabels.renewable_types.${renewableType.toLowerCase()}`),
  ]).join(" · ");
}

function mwhInputId(deviceId: number): string {
  return `${mwhIdPrefix}-${deviceId}`;
}

const { busy, run } = inject(sourceWritesKey)!;
</script>

<style scoped lang="scss">
.picker-list {
  max-height: 220px;
  overflow-y: auto;
}

.device-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 1rem;
}

.device-row-name {
  flex: 1 1 14rem;
  min-width: 0;
}

.device-row-controls {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 0.75rem;
  margin-left: auto;
}

.mwh-input {
  width: 11rem;

  input {
    -moz-appearance: textfield;
    appearance: textfield;
  }
}
</style>
